import React from 'react';
import { Button } from '../ui/button';
import { Share2, Mail, Heart, Eye, X } from 'lucide-react';
import { MonkeyLogo as PreviewMonkeyLogo } from '../MonkeyLogo';
import { StickerLayer } from '../StickerEditor';
import { getTheme } from '../../utils/themes';
import { BUTTON_STYLE_OPTIONS, getFontById, getAvatarShape } from '../../utils/customisation';
import { getWallpaperCSS, getWallpaperElements } from '../../utils/wallpapers';
import { socialPlatforms } from '../../utils/socialPlatforms';
import { getEmbedUrl, getEmbedLabel } from '../../utils/embedUtils';
import { getImageUrl } from '../../utils/api';

/* ─── Profile Preview Panel ─── */
export const ProfilePreview = ({ links, profilePicture, formData, username, onClose, stickerEdit, mode = 'mobile' }) => {
  const theme = getTheme(formData.theme);
  const ts = theme.styles;
  const picUrl = getImageUrl(profilePicture);
  const embeddableLinks = (links || []).filter(l => getEmbedUrl(l.url) && l.embed_enabled !== false);
  const allLinks = links || [];
  const displayName = formData.name || username || 'Profile';

  // Customisation from live formData
  const fontConfig = getFontById(formData.font);
  const btnStyle = BUTTON_STYLE_OPTIONS.find(b => b.id === (formData.button_style || 'rounded')) || BUTTON_STYLE_OPTIONS[0];
  const isLeft = formData.layout === 'left';
  const accent = formData.accent_color || ts['--theme-accent'];
  const wallpaperId = formData.wallpaper || 'none';
  // v2 customisation
  const shape = getAvatarShape(formData.avatar_shape);
  const avatarShapeStyle = {
    borderRadius: shape.borderRadius,
    ...(shape.clipPath ? { clipPath: shape.clipPath } : {}),
  };
  const cardStyle = formData.link_card_style || 'classic';
  const isGlass = cardStyle === 'glass';
  const isStacked = cardStyle === 'stacked';
  const bgEffect = formData.bg_effect || 'none';
  const orbitOn = !!formData.orbit_enabled;

  // Detect dark theme for icon color adjustment
  const isDarkTheme = (() => {
    const bg = ts['--theme-bg'] || '#ffffff';
    const hex = bg.replace('#', '');
    if (hex.length < 6) return false;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 128;
  })();
  const getIconColor = (color) => {
    if (!isDarkTheme || !color) return color;
    const hex = color.replace('#', '');
    if (hex.length < 6) return color;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 < 60 ? '#ffffff' : color;
  };

  const wpElements = getWallpaperElements(wallpaperId);
  const wpCSS = getWallpaperCSS(wallpaperId, accent);

  const previewContent = (
    <div className={`relative min-h-full ${wallpaperId !== 'none' ? 'wallpaper-container' : ''}`}
      style={{ background: ts['--theme-gradient'], backgroundColor: ts['--theme-bg'], fontFamily: `'${fontConfig.family}', sans-serif` }}>
      <style>{wpCSS}</style>
      {/* Pro custom wallpaper preview — uses position:sticky so it stays
          fixed to the visible phone frame as the user scrolls the inner
          content (instead of stretching to cover the whole scroll height). */}
      {formData.custom_wallpaper_url && (() => {
        const wScale = typeof formData.custom_wallpaper_scale === 'number' ? formData.custom_wallpaper_scale : 100;
        const wMode = formData.custom_wallpaper_mode || 'cover';
        const wBgSize = wMode === 'tile'
          ? `${wScale}% auto`
          : (wScale === 100 ? 'cover' : `${wScale}% auto`);
        return (
          <div aria-hidden className="pointer-events-none sticky top-0 left-0 w-full h-screen -mb-[100vh] z-0" style={{
            backgroundImage: `url(${formData.custom_wallpaper_url})`,
            backgroundSize: wBgSize,
            backgroundRepeat: wMode === 'tile' ? 'repeat' : 'no-repeat',
            backgroundPosition: 'center',
            opacity: (typeof formData.custom_wallpaper_opacity === 'number' ? formData.custom_wallpaper_opacity : 100) / 100,
            filter: (formData.custom_wallpaper_blur || 0) > 0 ? `blur(${formData.custom_wallpaper_blur}px)` : 'none',
          }} />
        );
      })()}
      {wpElements.map((el, i) => (
        <span key={i} className={el.className} aria-hidden="true" style={{ position: 'absolute' }}>{el.char}</span>
      ))}
      {/* v2 bg effect overlays */}
      {bgEffect === 'grain' && (
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
        }} />
      )}
      {bgEffect === 'starfield' && (
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          {Array.from({ length: 40 }).map((_, i) => (
            <span key={i} className="absolute rounded-full" style={{
              left: `${(i * 37) % 100}%`,
              top: `${(i * 71) % 100}%`,
              width: `${1 + (i % 3)}px`,
              height: `${1 + (i % 3)}px`,
              background: ts['--theme-text'],
              opacity: 0.3 + (i % 5) * 0.1,
            }} />
          ))}
        </div>
      )}
      {bgEffect === 'spotlight' && (
        <div aria-hidden className="pointer-events-none absolute inset-0" style={{
          background: `radial-gradient(400px circle at 50% 30%, ${accent}22, transparent 60%)`,
        }} />
      )}

      <div className="wallpaper-content relative max-w-lg mx-auto px-4 pt-8 pb-16">
        <StickerLayer stickers={formData.stickers || []} {...(stickerEdit || {})} />
        {/* Profile */}
        <div className={`${isLeft ? 'text-left' : 'text-center'} mb-8`}>
          {/* Full-width-square avatar (Pro) — bypasses the orbit/round renderers */}
          {shape.fullWidth && picUrl ? (
            <div className="relative -mx-4 -mt-4 mb-6 overflow-hidden" style={{ aspectRatio: '1 / 1', maxHeight: 420 }} data-testid="preview-fullwidth-square">
              <img src={picUrl} alt={formData.name} className="w-full h-full object-cover" />
              {/* All-edge inset vignette so top + sides blend, not just bottom */}
              <div className="absolute inset-0 pointer-events-none"
                style={{ boxShadow: `inset 0 0 80px 24px ${ts['--theme-bg'] || '#0a0a0a'}` }}
                aria-hidden="true" />
              <div className="absolute inset-x-0 bottom-0 pointer-events-none"
                style={{ height: '45%', background: `linear-gradient(to bottom, transparent 0%, ${ts['--theme-bg'] || '#0a0a0a'} 95%)` }}
                aria-hidden="true" />
            </div>
          ) : orbitOn ? (
            <div className="relative mx-auto w-32 h-32 mb-4">
              <div className="absolute inset-0 rounded-full border" style={{ borderColor: ts['--theme-card-border'], opacity: 0.6 }} />
              <div className="absolute inset-[12%] rounded-full border" style={{ borderColor: ts['--theme-card-border'], opacity: 0.4 }} />
              {/* spotlight under avatar */}
              <div className="absolute inset-[20%] rounded-full blur-2xl" style={{ background: (formData.orbit_spotlight_color || accent), opacity: 0.3 }} />
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16">
                {picUrl ? (
                  <img src={picUrl} alt={formData.name} className="w-full h-full object-cover shadow-xl"
                    style={{ border: `3px solid ${ts['--theme-card-border']}`, ...avatarShapeStyle }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xl font-bold shadow-xl"
                    style={{ background: accent, color: ts['--theme-accent-text'], border: `3px solid ${ts['--theme-card-border']}`, ...avatarShapeStyle }}>
                    {formData.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
              </div>
              {/* tiny orbiting platform dots — visual hint only */}
              {(links || []).slice(0, 6).map((l, i) => {
                const platform = socialPlatforms.find(p => p.id === l.platform);
                const angle = -90 + (360 / Math.min(6, (links || []).length || 1)) * i;
                const rad = (angle * Math.PI) / 180;
                const left = 50 + Math.cos(rad) * 45;
                const top = 50 + Math.sin(rad) * 45;
                const col = l.color || platform?.color || accent;
                return (
                  <div key={i} className="absolute w-5 h-5 rounded-md flex items-center justify-center"
                    style={{ left: `${left}%`, top: `${top}%`, transform: 'translate(-50%, -50%)', background: `${col}25`, boxShadow: `0 0 8px ${col}88` }}>
                    {platform?.icon && <platform.icon className="w-3 h-3" style={{ color: col }} />}
                  </div>
                );
              })}
            </div>
          ) : (
            picUrl ? (
              <img src={picUrl} alt={formData.name} className={`w-24 h-24 object-cover ${isLeft ? '' : 'mx-auto'} mb-4 shadow-xl`}
                style={{ border: `4px solid ${ts['--theme-card-border']}`, ...avatarShapeStyle }} />
            ) : (
              <div className={`inline-flex items-center justify-center w-24 h-24 text-3xl font-bold ${isLeft ? '' : 'mx-auto'} mb-4 shadow-xl`}
                style={{ background: accent, color: ts['--theme-accent-text'], border: `4px solid ${ts['--theme-card-border']}`, ...avatarShapeStyle }}>
                {formData.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )
          )}
          <h1 className="text-2xl font-bold mb-1" style={{ color: ts['--theme-text'] }}>{formData.name || 'Your Name'}</h1>
          <p className="text-sm mb-3" style={{ color: ts['--theme-text-muted'] }}>@{username}</p>
          {formData.bio && (
            <p className={`text-sm mb-4 ${isLeft ? '' : 'max-w-sm mx-auto'} leading-relaxed`} style={{ color: ts['--theme-text'] }}>{formData.bio}</p>
          )}
          <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium"
            style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}40` }}>
            <Share2 className="w-4 h-4 mr-2" /> Share Profile
          </span>
        </div>

        {/* Email Signup Widget — mirrors PublicProfile */}
        {formData.email_signup_enabled && (
          <div className="mb-6 p-4 rounded-xl text-center" style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }}>
            <Mail className="w-5 h-5 mx-auto mb-2" style={{ color: accent }} />
            <p className="text-sm font-semibold mb-1" style={{ color: ts['--theme-text'] }}>
              {formData.email_signup_title || 'Stay Connected'}
            </p>
            <p className="text-xs mb-3" style={{ color: ts['--theme-text-muted'] }}>
              {formData.email_signup_subtitle || 'Get updates straight to your inbox'}
            </p>
            <div className="flex gap-2 max-w-sm mx-auto">
              <div className="flex-1 px-3 py-2 text-sm rounded-lg border"
                style={{ borderColor: ts['--theme-card-border'], color: ts['--theme-text-muted'], background: 'transparent' }}>
                your@email.com
              </div>
              <div className="px-4 py-2 text-sm font-medium rounded-lg"
                style={{ background: accent, color: ts['--theme-accent-text'] || '#fff' }}>
                {formData.email_signup_button || 'Subscribe'}
              </div>
            </div>
          </div>
        )}

        {/* Tip Jar Widget — mirrors PublicProfile */}
        {formData.tip_jar_enabled && formData.tip_jar_paypal && (
          <div className="mb-6 p-4 rounded-xl text-center" style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }}>
            <Heart className="w-5 h-5 mx-auto mb-2" style={{ color: '#EC4899' }} />
            <p className="text-sm font-semibold mb-1" style={{ color: ts['--theme-text'] }}>
              {formData.tip_jar_title || 'Tip Jar'}
            </p>
            <p className="text-xs mb-3" style={{ color: ts['--theme-text-muted'] }}>
              {formData.tip_jar_subtitle || `Support ${formData.name || username} with a tip`}
            </p>
            {formData.tip_jar_show_balance && (
              <div className="mb-3 max-w-sm mx-auto">
                {formData.tip_jar_goal && formData.tip_jar_goal > 0 ? (
                  <>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-lg font-bold tabular-nums" style={{ color: ts['--theme-text'] }}>
                        {(formData.tip_jar_currency === 'EUR' ? '€' : formData.tip_jar_currency === 'USD' ? '$' : formData.tip_jar_currency === 'GBP' ? '£' : '€') + Math.round(formData.tip_jar_balance || 0)}
                      </span>
                      <span className="text-xs tabular-nums" style={{ color: ts['--theme-text-muted'] }}>
                        of {(formData.tip_jar_currency === 'EUR' ? '€' : formData.tip_jar_currency === 'USD' ? '$' : formData.tip_jar_currency === 'GBP' ? '£' : '€')}{formData.tip_jar_goal}
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: ts['--theme-card-border'] }}>
                      <div className="h-full rounded-full"
                        style={{
                          width: `${Math.min(100, ((formData.tip_jar_balance || 0) / formData.tip_jar_goal) * 100)}%`,
                          background: 'linear-gradient(90deg, #EC4899, #F472B6)'
                        }} />
                    </div>
                  </>
                ) : (
                  <div className="inline-flex items-baseline gap-1.5 px-3 py-1.5 rounded-full" style={{ background: '#EC489918', border: '1px solid #EC489940' }}>
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: ts['--theme-text-muted'] }}>raised</span>
                    <span className="text-base font-bold tabular-nums" style={{ color: '#EC4899' }}>
                      {(formData.tip_jar_currency === 'EUR' ? '€' : formData.tip_jar_currency === 'USD' ? '$' : formData.tip_jar_currency === 'GBP' ? '£' : '€') + Math.round(formData.tip_jar_balance || 0)}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-center">
              {[1, 3, 5, 10].map(amt => (
                <div key={amt} className="px-4 py-2 text-sm font-medium rounded-lg"
                  style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}40` }}>
                  {(formData.tip_jar_currency === 'EUR' ? '€' : formData.tip_jar_currency === 'USD' ? '$' : formData.tip_jar_currency === 'GBP' ? '£' : '€')}{amt}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Social Icons — only recognised social platforms (mirrors public profile) */}
        {(() => {
          const NON_SOCIAL_IDS = new Set(['custom', 'email', 'phone', 'sms', 'other']);
          const socialOnly = (allLinks || []).filter(l => {
            if (NON_SOCIAL_IDS.has(l.platform)) return false;
            if (l.image_url && l.platform !== 'website') return false;
            // Note: embeddable links DO appear in the top row when not gated
            // out by layout — this matches PublicProfile behaviour exactly.
            if (l.layout === 'icon_in_button') return false;
            return !!socialPlatforms.find(p => p.id === l.platform);
          });
          if (socialOnly.length === 0) return null;
          return (
            <div className="mb-8">
              <div className={`flex flex-wrap ${isLeft ? 'justify-start' : 'justify-center'} gap-3`}>
                {socialOnly.map((link) => {
                  const platform = socialPlatforms.find(p => p.id === link.platform);
                  const Icon = platform?.icon;
                  return (
                    <div key={link.id} className={`flex items-center justify-center w-14 h-14 overflow-hidden ${isGlass ? 'backdrop-blur-xl' : ''} ${isStacked ? 'shadow-[0_4px_0_0_rgba(0,0,0,0.18),0_10px_24px_rgba(0,0,0,0.25)]' : ''}`}
                      style={{
                        background: isGlass ? `${getIconColor(link.color)}22` : `${getIconColor(link.color)}18`,
                        border: btnStyle.border ? `${isGlass ? '1px' : '2px'} solid ${getIconColor(link.color)}${isGlass ? '50' : '30'}` : 'none',
                        borderRadius: btnStyle.borderRadius
                      }}>
                      {link.image_url ? (
                        <img src={link.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        Icon && <Icon className="w-7 h-7" style={{ color: getIconColor(link.color) }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Unified ordered stack: embeds + rich cards (mirrors public profile) */}
        {(() => {
          const NON_SOCIAL_IDS = new Set(['custom', 'email', 'phone', 'sms', 'other']);
          const stack = (allLinks || []).filter(l => {
            const emb = !!getEmbedUrl(l.url) && l.embed_enabled !== false;
            if (emb) return true;
            if (l.layout === 'icon_in_button' || l.layout === 'both') return true;
            if (l.platform === 'website') return false;
            if (l.image_url) return true;
            if (NON_SOCIAL_IDS.has(l.platform)) return true;
            return false;
          });
          if (stack.length === 0) return null;
          return (
            <div className="space-y-4 mb-6">
              {stack.map((link) => {
                const embed = getEmbedUrl(link.url);
                const platform = socialPlatforms.find(p => p.id === link.platform);
                const Icon = platform?.icon;
                if (embed && link.embed_enabled !== false) {
                  return (
                    <div key={link.id} className="rounded-xl overflow-hidden"
                      style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }}>
                      <div className="p-3 pb-2 flex items-center space-x-3">
                        {Icon && (
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${link.color}20` }}>
                            <Icon className="w-4 h-4" style={{ color: link.color }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate" style={{ color: ts['--theme-text'] }}>{link.title}</p>
                          <p className="text-xs" style={{ color: ts['--theme-text-muted'] }}>{getEmbedLabel(link.url) || link.platformName}</p>
                        </div>
                      </div>
                      <div className="px-3 pb-3">
                        <div className="rounded-lg overflow-hidden" style={embed.aspectRatio ? { aspectRatio: embed.aspectRatio } : { height: embed.height || 300 }}>
                          <iframe src={embed.embedUrl} title={link.title} className="w-full h-full border-0 rounded-lg"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen loading="lazy" />
                        </div>
                      </div>
                    </div>
                  );
                }
                // Per-link layout switch (mirrors PublicProfile)
                // 'icon_in_button' OR 'both' → Linktree-style pill.
                if (link.layout === 'icon_in_button' || link.layout === 'both') {
                  const baseStyle = { background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}`, borderRadius: btnStyle.borderRadius };
                  return (
                    <div key={link.id} className="w-full relative flex items-center gap-3 px-4 py-3" style={baseStyle}>
                      <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
                        style={{ background: link.image_url ? 'transparent' : `${link.color || accent}22` }}>
                        {link.image_url ? (
                          <img src={link.image_url} alt="" className="w-full h-full object-cover" />
                        ) : (Icon && <Icon className="w-5 h-5" style={{ color: link.color || accent }} />)}
                      </span>
                      <span className="flex-1 text-center font-semibold text-sm" style={{ color: ts['--theme-text'] }}>
                        {link.title || link.platformName || 'Link'}
                      </span>
                      <span className="w-10 h-10 flex-shrink-0" aria-hidden="true" />
                    </div>
                  );
                }

                // Default: legacy rich card for image-bearing or generic links
                const ctaText = (link.cta_text && link.cta_text.trim()) || 'Open';
                const ctaColor = link.cta_color || link.color || accent;
                return (
                  <div key={link.id} className="rounded-2xl overflow-hidden"
                    style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }}>
                    <div className="p-3 sm:p-4 flex items-start gap-3 sm:gap-4">
                      {link.image_url ? (
                        <img src={link.image_url} alt="" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-20 h-20 rounded-xl flex-shrink-0 flex items-center justify-center"
                          style={{ background: `${link.color || accent}22` }}>
                          {Icon && <Icon className="w-9 h-9" style={{ color: link.color || accent }} />}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-base leading-tight mb-1" style={{ color: ts['--theme-text'] }}>{link.title || link.platformName || 'Link'}</p>
                        {link.description && (
                          <p className="text-xs leading-relaxed line-clamp-3" style={{ color: ts['--theme-text-muted'] }}>{link.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="px-3 pb-3">
                      <div className="w-full py-3 rounded-xl font-semibold text-sm text-center"
                        style={{ background: ctaColor, color: '#000', borderRadius: btnStyle.borderRadius }}>
                        {ctaText}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {(!links || links.length === 0) && (
          <div className="p-12 text-center rounded-xl" style={{ background: ts['--theme-card'], border: `1px solid ${ts['--theme-card-border']}` }}>
            <p style={{ color: ts['--theme-text-muted'] }}>No links added yet</p>
          </div>
        )}

        <div className="mt-12 pt-6" style={{ borderTop: `1px solid ${ts['--theme-card-border']}` }}>
          <div className="flex items-center justify-center space-x-2 opacity-60">
            <PreviewMonkeyLogo size={20} />
            <span className="text-sm font-medium" style={{ color: ts['--theme-text-muted'] }}>Powered by LinkMonkey</span>
          </div>
        </div>
      </div>
    </div>
  );

  const isDesktopMode = mode === 'desktop';
  const panelWidth = isDesktopMode ? 'w-[640px]' : 'w-[420px]';

  return (
    <>
      {/* Desktop side panel */}
      <div className={`hidden lg:block fixed top-16 right-0 bottom-0 ${panelWidth} border-l border-border bg-muted/30 z-40 animate-in slide-in-from-right duration-300`}
        data-testid="preview-panel-desktop">
        <div className="flex items-center justify-between px-4 py-3 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">Live Preview</span>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5">
              {isDesktopMode ? 'Desktop' : 'Mobile'}
            </span>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} data-testid="close-preview-desktop">
            <X className="w-4 h-4" />
          </Button>
        </div>
        {/* Device mockup frame */}
        <div className="flex items-start justify-center p-4 h-[calc(100%-52px)] overflow-hidden">
          {isDesktopMode ? (
            // Desktop browser frame (macOS style chrome)
            <div className="w-full h-full rounded-lg border border-zinc-700 bg-zinc-900 shadow-2xl overflow-hidden flex flex-col" data-testid="preview-frame-desktop">
              <div className="h-8 bg-zinc-800 flex items-center px-3 gap-1.5 border-b border-zinc-700">
                <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                <div className="flex-1 flex justify-center">
                  <span className="text-[11px] text-zinc-400 bg-zinc-900 rounded-md px-3 py-0.5 max-w-[70%] truncate">linkmonkey.io/{username}</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto bg-white">
                {previewContent}
              </div>
            </div>
          ) : (
            // Phone frame
            <div className="w-[340px] h-full rounded-[2rem] border-[6px] border-zinc-800 bg-zinc-800 shadow-2xl overflow-hidden flex flex-col" data-testid="preview-frame-mobile">
              {/* Phone notch */}
              <div className="h-6 bg-zinc-800 flex items-center justify-center">
                <div className="w-24 h-3 rounded-full bg-zinc-900" />
              </div>
              <div className="flex-1 overflow-y-auto">
                {previewContent}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile full-screen overlay */}
      <div className="lg:hidden fixed inset-0 z-50 bg-background animate-in fade-in duration-200" data-testid="preview-panel-mobile">
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold">Preview</span>
          </div>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="close-preview-mobile">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
        <div className="h-[calc(100vh-52px)] overflow-y-auto">
          {previewContent}
        </div>
      </div>
    </>
  );
};
